import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface StageHeadingProps {
    /** The small letterspaced kicker above the headline. */
    label?: string
    /** The setup line, half-lit. */
    small: string
    /** The payoff line, full size. */
    large: string
    level?: 1 | 2
    className?: string
}

/* The section's own headline shape, kept: one sentence broken over two lines,
 * the setup at half opacity and the payoff twice its size, so a glance reads
 * only the big half and that half still makes sense on its own. */
export const StageHeading: FC<StageHeadingProps> = ({
    label,
    small,
    large,
    level = 2,
    className
}): ReactNode => {
    const Tag = level === 1 ? 'h1' : 'h2'

    return (
        <div className={cn('flex flex-col', className)}>
            {label && (
                <span className='mb-6 inline-flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#F4F1EA]/50'>
                    <i aria-hidden className='not-italic opacity-70'>✳</i>
                    {label}
                </span>
            )}

            <Tag className='flex flex-col font-heading font-normal leading-[0.92] tracking-[-0.01em]'>
                <span
                    className={cn(
                        'opacity-50',
                        level === 1
                            ? 'text-[clamp(26px,4.2vw,54px)]'
                            : 'text-[clamp(20px,2.6vw,32px)]'
                    )}
                >
                    {small}
                </span>

                <em
                    className={cn(
                        'not-italic',
                        level === 1
                            ? 'text-[clamp(36px,6.6vw,88px)]'
                            : 'text-[clamp(28px,4vw,54px)]'
                    )}
                >
                    {large}
                </em>
            </Tag>
        </div>
    )
}
